import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import {
  ArrowLeft, LogOut, Plus, Download, MessageCircle, Trash2,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  FileText, RefreshCw, CalendarDays, ArrowUpDown,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VedLogo from '../../components/VedLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  confirmed: 'bg-green-100 text-green-700',
  checked_in: 'bg-blue-100 text-blue-700',
  checked_out: 'bg-gray-100 text-gray-700',
  cancelled: 'bg-red-100 text-red-700',
};

const TABS = ['Create Booking', 'All Bookings', 'Calendar'];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* ──────────────────────────────────────────────────────────────────────────
   Room Row inside Create Booking form
────────────────────────────────────────────────────────────────────────── */
const RoomRow = ({ index, allRooms, roomEntry, onChange, onRemove }) => (
  <div className="flex gap-3 items-end mb-3">
    <div className="flex-1">
      {index === 0 && <label className="block text-xs font-medium text-gray-600 mb-1">Room</label>}
      <select
        value={roomEntry.room_id}
        onChange={e => onChange(index, 'room_id', e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
      >
        <option value="">Select room…</option>
        {allRooms.map(r => (
          <option key={r.id} value={r.id}>{r.name}</option>
        ))}
      </select>
    </div>
    <div className="w-40">
      {index === 0 && <label className="block text-xs font-medium text-gray-600 mb-1">Price / Night (₹)</label>}
      <input
        type="number"
        min="0"
        value={roomEntry.price_per_night}
        onChange={e => onChange(index, 'price_per_night', parseFloat(e.target.value) || 0)}
        placeholder="e.g. 1500"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
      />
    </div>
    {index > 0 && (
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="mb-0.5 text-red-500 hover:text-red-700 transition-colors"
      >
        <Trash2 size={16} />
      </button>
    )}
  </div>
);

/* ──────────────────────────────────────────────────────────────────────────
   Create Booking Tab
────────────────────────────────────────────────────────────────────────── */
const CreateBookingTab = ({ allRooms, onCreated }) => {
  const navigate = useNavigate();
  const emptyRoom = { room_id: '', price_per_night: '' };
  const [form, setForm] = useState({
    guest_name: '',
    guest_phone: '',
    guest_email: '',
    guest_address: '',
    check_in: '',
    check_out: '',
    num_adults: 1,
    num_children: 0,
    advance_received: '',
    notes: '',
  });
  const [rooms, setRooms] = useState([{ ...emptyRoom }]);
  const [submitting, setSubmitting] = useState(false);

  const numNights =
    form.check_in && form.check_out
      ? Math.max(
          0,
          Math.round(
            (new Date(form.check_out) - new Date(form.check_in)) / 86400000
          )
        )
      : 0;

  const totalAmount = rooms.reduce(
    (sum, r) => sum + (parseFloat(r.price_per_night) || 0) * numNights,
    0
  );

  const handleFormChange = (field, value) =>
    setForm(prev => ({ ...prev, [field]: value }));

  const handleRoomChange = (index, field, value) =>
    setRooms(prev => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));

  const addRoom = () => setRooms(prev => [...prev, { ...emptyRoom }]);
  const removeRoom = index => setRooms(prev => prev.filter((_, i) => i !== index));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.guest_name.trim() || !form.guest_phone.trim()) {
      toast.error('Guest name and phone are required');
      return;
    }
    if (!form.check_in || !form.check_out) {
      toast.error('Check-in and check-out dates are required');
      return;
    }
    if (numNights <= 0) {
      toast.error('Check-out must be after check-in');
      return;
    }
    const validRooms = rooms.filter(r => r.room_id && parseFloat(r.price_per_night) > 0);
    if (validRooms.length === 0) {
      toast.error('Please add at least one room with a valid price');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('admin_token');
      const payload = {
        ...form,
        advance_received: parseFloat(form.advance_received) || 0,
        num_adults: parseInt(form.num_adults) || 1,
        num_children: parseInt(form.num_children) || 0,
        rooms: validRooms.map(r => ({
          room_id: r.room_id,
          price_per_night: parseFloat(r.price_per_night),
        })),
      };
      const res = await axios.post(`${BACKEND_URL}/api/admin/bookings/direct`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Booking ${res.data.booking_ref} created successfully!`);
      // Reset form
      setForm({ guest_name: '', guest_phone: '', guest_email: '', guest_address: '', check_in: '', check_out: '', num_adults: 1, num_children: 0, advance_received: '', notes: '' });
      setRooms([{ ...emptyRoom }]);
      onCreated();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create booking');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-lg font-bold text-gray-800 mb-6">New Direct Booking</h2>

      {/* Guest Info */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Guest Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={form.guest_name}
            onChange={e => handleFormChange('guest_name', e.target.value)}
            placeholder="Full name"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Phone <span className="text-red-500">*</span>
          </label>
          <input
            type="tel"
            value={form.guest_phone}
            onChange={e => handleFormChange('guest_phone', e.target.value)}
            placeholder="+91 XXXXXXXXXX"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input
            type="email"
            value={form.guest_email}
            onChange={e => handleFormChange('guest_email', e.target.value)}
            placeholder="guest@email.com"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <input
            type="text"
            value={form.guest_address}
            onChange={e => handleFormChange('guest_address', e.target.value)}
            placeholder="City, State"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      {/* Guests */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Number of Adults</label>
          <input
            type="number"
            min="1"
            value={form.num_adults}
            onChange={e => handleFormChange('num_adults', parseInt(e.target.value) || 1)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Number of Children</label>
          <input
            type="number"
            min="0"
            value={form.num_children}
            onChange={e => handleFormChange('num_children', parseInt(e.target.value) || 0)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Check-in Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={form.check_in}
            onChange={e => handleFormChange('check_in', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Check-out Date <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={form.check_out}
            min={form.check_in || undefined}
            onChange={e => handleFormChange('check_out', e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>
      </div>

      {numNights > 0 && (
        <p className="text-sm text-orange-600 font-medium mb-4">
          Duration: {numNights} night{numNights !== 1 ? 's' : ''}
        </p>
      )}

      {/* Rooms */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">Rooms <span className="text-red-500">*</span></label>
          <button
            type="button"
            onClick={addRoom}
            className="text-sm text-orange-600 hover:text-orange-700 flex items-center gap-1"
          >
            <Plus size={14} /> Add Room
          </button>
        </div>
        {rooms.map((r, idx) => (
          <RoomRow
            key={idx}
            index={idx}
            allRooms={allRooms}
            roomEntry={r}
            onChange={handleRoomChange}
            onRemove={removeRoom}
          />
        ))}
      </div>

      {/* Total Preview */}
      {numNights > 0 && totalAmount > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700 font-medium">Estimated Total ({numNights} nights)</span>
            <span className="text-xl font-bold text-orange-600">₹{totalAmount.toLocaleString('en-IN')}</span>
          </div>
          {parseFloat(form.advance_received) > 0 && (
            <>
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-700 font-medium">Advance Received</span>
                <span className="text-base font-semibold text-green-600">− ₹{parseFloat(form.advance_received).toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between items-center border-t border-orange-200 pt-2">
                <span className="text-sm text-gray-700 font-bold">Balance Due at Checkout</span>
                <span className="text-lg font-bold text-orange-700">₹{Math.max(0, totalAmount - parseFloat(form.advance_received)).toLocaleString('en-IN')}</span>
              </div>
              {parseFloat(form.advance_received) > totalAmount && (
                <p className="text-xs text-red-500 mt-1">Advance exceeds total amount</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Advance Received */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Advance Received (₹)</label>
        <input
          type="number"
          min="0"
          value={form.advance_received}
          onChange={e => handleFormChange('advance_received', e.target.value)}
          placeholder="0"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
        />
      </div>

      {/* Notes */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes / Special Requests</label>
        <textarea
          value={form.notes}
          onChange={e => handleFormChange('notes', e.target.value)}
          rows={3}
          placeholder="Any special requests or notes…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={submitting}
        className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
      >
        {submitting ? <RefreshCw size={16} className="animate-spin" /> : <Plus size={16} />}
        {submitting ? 'Creating Booking…' : 'Create Booking & Generate PDF'}
      </button>
    </form>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Single Booking Card in list
────────────────────────────────────────────────────────────────────────── */
const BookingCard = ({ booking, onStatusChange, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [sendingWA, setSendingWA] = useState(false);

  const handleDownloadPdfAuth = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/bookings/direct/${booking.id}/pdf`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const safeRef = (booking.booking_ref || booking.id).replace(/[^a-zA-Z0-9_-]/g, '_');
      link.setAttribute('download', `booking_${safeRef}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const handleWhatsApp = async () => {
    setSendingWA(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/bookings/direct/${booking.id}/whatsapp-link`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(res.data.whatsapp_url, '_blank');
    } catch {
      toast.error('Failed to generate WhatsApp link');
    } finally {
      setSendingWA(false);
    }
  };

  const balance = booking.total_amount - (booking.advance_received || 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Collapsed header */}
      <div
        className="p-5 flex items-start justify-between gap-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(v => !v)}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="font-mono text-sm font-bold text-orange-600">
              {booking.booking_ref}
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[booking.status] || STATUS_COLORS.confirmed}`}>
              {booking.status.replace('_', ' ')}
            </span>
          </div>
          <p className="text-base font-semibold text-gray-800 mt-0.5">{booking.guest_name}</p>
          <p className="text-sm text-gray-500">
            {booking.check_in} → {booking.check_out} · {booking.num_nights} night{booking.num_nights !== 1 ? 's' : ''}
          </p>
          <p className="text-sm text-gray-700 font-medium mt-1">
            ₹{booking.total_amount.toLocaleString('en-IN')}
            {balance > 0 && (
              <span className="text-orange-600 text-xs ml-2">(₹{balance.toLocaleString('en-IN')} due)</span>
            )}
          </p>
        </div>
        <div className="shrink-0 text-gray-400">
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-gray-100 px-5 py-4 space-y-4">
          {/* Guest info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <p><span className="text-gray-500">Phone:</span> <span className="font-medium">{booking.guest_phone}</span></p>
            {booking.guest_email && (
              <p><span className="text-gray-500">Email:</span> <span className="font-medium">{booking.guest_email}</span></p>
            )}
            {booking.guest_address && (
              <p><span className="text-gray-500">Address:</span> <span className="font-medium">{booking.guest_address}</span></p>
            )}
            <p>
              <span className="text-gray-500">Guests:</span>{' '}
              <span className="font-medium">
                {booking.num_adults || 1} Adult{(booking.num_adults || 1) !== 1 ? 's' : ''}
                {booking.num_children > 0 ? ` + ${booking.num_children} Child${booking.num_children !== 1 ? 'ren' : ''}` : ''}
              </span>
            </p>
            <p>
              <span className="text-gray-500">Created:</span>{' '}
              <span className="font-medium">{new Date(booking.created_at).toLocaleDateString()}</span>
            </p>
          </div>

          {/* Rooms */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Rooms</p>
            <div className="divide-y divide-gray-100 rounded-lg border border-gray-100 overflow-hidden">
              {booking.rooms.map((r, i) => (
                <div key={i} className="flex justify-between items-center px-3 py-2 text-sm bg-gray-50">
                  <span className="font-medium text-gray-700">{r.room_name}</span>
                  <span className="text-gray-600">
                    ₹{r.price_per_night.toLocaleString('en-IN')}/night × {booking.num_nights} ={' '}
                    <strong>₹{r.total_price.toLocaleString('en-IN')}</strong>
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Payment */}
          <div className="text-sm space-y-1">
            <p>
              <span className="text-gray-500">Total:</span>{' '}
              <span className="font-bold">₹{booking.total_amount.toLocaleString('en-IN')}</span>
            </p>
            <p>
              <span className="text-gray-500">Advance Received:</span>{' '}
              <span className="font-medium text-green-600">₹{(booking.advance_received || 0).toLocaleString('en-IN')}</span>
            </p>
            {balance > 0 && (
              <p>
                <span className="text-gray-500">Balance Due at Checkout:</span>{' '}
                <span className="font-bold text-orange-600">₹{balance.toLocaleString('en-IN')}</span>
              </p>
            )}
          </div>

          {booking.notes && (
            <p className="text-sm text-gray-600 bg-yellow-50 border border-yellow-100 rounded-lg px-3 py-2">
              <span className="font-medium text-gray-700">Notes:</span> {booking.notes}
            </p>
          )}

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            <select
              value={booking.status}
              onChange={e => onStatusChange(booking.id, e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="confirmed">Confirmed</option>
              <option value="checked_in">Checked In</option>
              <option value="checked_out">Checked Out</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <button
              onClick={handleDownloadPdfAuth}
              className="flex items-center gap-1.5 text-sm border border-blue-200 text-blue-600 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors"
            >
              <Download size={14} /> Download PDF
            </button>

            <button
              onClick={handleWhatsApp}
              disabled={sendingWA}
              className="flex items-center gap-1.5 text-sm border border-green-200 text-green-600 hover:bg-green-50 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
            >
              <MessageCircle size={14} />
              {sendingWA ? 'Opening…' : 'Send via WhatsApp'}
            </button>

            <button
              onClick={() => onDelete(booking.id, booking.booking_ref)}
              className="flex items-center gap-1.5 text-sm border border-red-200 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-lg transition-colors ml-auto"
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Calendar Tab – monthly grid showing direct-booking guest info per day
────────────────────────────────────────────────────────────────────────── */
const CalendarTab = ({ bookings }) => {
  const today = new Date();
  const todayStr = today.toISOString().split('T')[0];
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState(todayStr);

  const fmtDate = (y, m, d) =>
    `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

  const getBookingsForDate = dateStr =>
    bookings.filter(
      b => b.status !== 'cancelled' && b.check_in <= dateStr && b.check_out >= dateStr
    );

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = [
    ...Array(firstDayOfWeek).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const selectedBookings = selectedDate ? getBookingsForDate(selectedDate) : [];

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm px-5 py-3 mb-4">
        <button
          onClick={prevMonth}
          className="text-gray-500 hover:text-orange-600 transition-colors p-1 rounded-lg hover:bg-orange-50"
        >
          <ChevronLeft size={20} />
        </button>
        <h3 className="text-base font-bold text-gray-800">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </h3>
        <button
          onClick={nextMonth}
          className="text-gray-500 hover:text-orange-600 transition-colors p-1 rounded-lg hover:bg-orange-50"
        >
          <ChevronRight size={20} />
        </button>
      </div>

      {/* Calendar grid */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
        {/* Day-of-week headers */}
        <div className="grid grid-cols-7 bg-orange-600">
          {DAY_NAMES.map(d => (
            <div key={d} className="text-center text-xs font-semibold text-white py-2">{d}</div>
          ))}
        </div>

        {/* Day cells */}
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (!day) {
              return (
                <div
                  key={`empty-${idx}`}
                  className="min-h-[72px] border-b border-r border-gray-100 bg-gray-50"
                />
              );
            }
            const dateStr = fmtDate(viewYear, viewMonth, day);
            const dayBookings = getBookingsForDate(dateStr);
            const isToday = dateStr === todayStr;
            const isSelected = dateStr === selectedDate;

            return (
              <div
                key={dateStr}
                onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                className={`min-h-[72px] border-b border-r border-gray-100 p-1.5 cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-orange-50 ring-2 ring-inset ring-orange-400'
                    : dayBookings.length > 0
                    ? 'hover:bg-purple-50'
                    : 'hover:bg-gray-50'
                }`}
              >
                <span
                  className={`inline-flex w-6 h-6 items-center justify-center rounded-full text-xs font-semibold mb-1 ${
                    isToday ? 'bg-orange-600 text-white' : 'text-gray-700'
                  }`}
                >
                  {day}
                </span>
                <div className="space-y-0.5">
                  {dayBookings.slice(0, 2).map((b, i) => (
                    <div
                      key={i}
                      className="text-xs bg-purple-100 text-purple-800 rounded px-1 py-0.5 truncate leading-tight"
                    >
                      {b.guest_name}
                    </div>
                  ))}
                  {dayBookings.length > 2 && (
                    <div className="text-xs text-gray-400 leading-tight">
                      +{dayBookings.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected date detail panel */}
      {selectedDate && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
            <CalendarDays size={16} className="text-orange-600" />
            {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-IN', {
              weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
            })}
          </h4>

          {selectedBookings.length === 0 ? (
            <p className="text-sm text-gray-400">No direct bookings on this date.</p>
          ) : (
            <div className="space-y-3">
              {selectedBookings.map(b => (
                <div key={b.id} className="border border-purple-100 rounded-lg p-4 bg-purple-50">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="font-mono text-xs font-bold text-orange-600">
                      {b.booking_ref}
                    </span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        STATUS_COLORS[b.status] || STATUS_COLORS.confirmed
                      }`}
                    >
                      {b.status.replace('_', ' ')}
                    </span>
                    {b.check_in === selectedDate && (
                      <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium">
                        ✈ Check-in today
                      </span>
                    )}
                    {b.check_out === selectedDate && (
                      <span className="text-xs bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full font-medium">
                        🚪 Check-out today
                      </span>
                    )}
                  </div>

                  <p className="font-bold text-gray-900 text-base">{b.guest_name}</p>
                  <p className="text-sm text-gray-700 mt-0.5">📞 {b.guest_phone}</p>
                  {b.guest_email && (
                    <p className="text-sm text-gray-600">✉ {b.guest_email}</p>
                  )}

                  <div className="mt-2 pt-2 border-t border-purple-200 text-xs text-gray-600 space-y-0.5">
                    <p>
                      <span className="font-medium">Check-in:</span> {b.check_in}
                      &nbsp;→&nbsp;
                      <span className="font-medium">Check-out:</span> {b.check_out}
                      &nbsp;·&nbsp;
                      {b.num_nights} night{b.num_nights !== 1 ? 's' : ''}
                    </p>
                    <p>
                      <span className="font-medium">Rooms:</span>{' '}
                      {b.rooms.map(r => r.room_name).join(', ')}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   All Bookings Tab
────────────────────────────────────────────────────────────────────────── */
const AllBookingsTab = ({ bookings, loading, onRefresh, onStatusChange, onDelete }) => {
  const [filterStatus, setFilterStatus] = useState('');
  const [sortBy, setSortBy] = useState('booked_date_desc');

  const filtered = filterStatus
    ? bookings.filter(b => b.status === filterStatus)
    : bookings;

  const sorted = [...filtered].sort((a, b) => {
    switch (sortBy) {
      case 'booked_date_asc':  return new Date(a.created_at) - new Date(b.created_at);
      case 'booked_date_desc': return new Date(b.created_at) - new Date(a.created_at);
      case 'checkin_asc':      return a.check_in.localeCompare(b.check_in);
      case 'checkin_desc':     return b.check_in.localeCompare(a.check_in);
      case 'checkout_asc':     return a.check_out.localeCompare(b.check_out);
      case 'checkout_desc':    return b.check_out.localeCompare(a.check_out);
      default:                 return new Date(b.created_at) - new Date(a.created_at);
    }
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="checked_in">Checked In</option>
            <option value="checked_out">Checked Out</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 flex items-center gap-1"
          >
            <option value="booked_date_desc">Booked Date (Newest)</option>
            <option value="booked_date_asc">Booked Date (Oldest)</option>
            <option value="checkin_asc">Check-in (Earliest)</option>
            <option value="checkin_desc">Check-in (Latest)</option>
            <option value="checkout_asc">Check-out (Earliest)</option>
            <option value="checkout_desc">Check-out (Latest)</option>
          </select>
          <span className="text-sm text-gray-500">{sorted.length} booking{sorted.length !== 1 ? 's' : ''}</span>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-orange-600 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading…</div>
      ) : sorted.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center text-gray-400">
          <FileText size={48} className="mx-auto mb-3 opacity-30" />
          <p>No bookings found.</p>
          <p className="text-sm mt-1">Create a booking using the <strong>Create Booking</strong> tab.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sorted.map(b => (
            <BookingCard
              key={b.id}
              booking={b}
              onStatusChange={onStatusChange}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ──────────────────────────────────────────────────────────────────────────
   Main Page
────────────────────────────────────────────────────────────────────────── */
const AdminBookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(location.state?.tab ?? 0);
  const [allRooms, setAllRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const username = localStorage.getItem('admin_username');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin/login'); return; }
    fetchRooms();
    fetchBookings();
  }, [navigate]);

  const fetchRooms = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/rooms`);
      setAllRooms(res.data);
    } catch {
      // rooms not critical here
    }
  };

  const fetchBookings = useCallback(async () => {
    setBookingsLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.get(`${BACKEND_URL}/api/admin/bookings/direct`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(res.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired.');
        navigate('/admin/login');
      }
    } finally {
      setBookingsLoading(false);
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const handleBookingCreated = () => {
    fetchBookings();
    setActiveTab(1);
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${BACKEND_URL}/api/admin/bookings/direct/${bookingId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated');
      fetchBookings();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (bookingId, bookingRef) => {
    if (!window.confirm(`Delete booking ${bookingRef}? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/bookings/direct/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Booking deleted');
      fetchBookings();
    } catch {
      toast.error('Failed to delete booking');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <VedLogo size="sm" showText={false} />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Direct Bookings</h1>
              <p className="text-sm text-gray-600">Welcome, {username}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 text-gray-600 hover:text-red-600 transition-colors"
          >
            <LogOut size={18} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <Link
            to="/admin/dashboard"
            className="flex items-center space-x-2 text-gray-600 hover:text-orange-600 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back to Dashboard</span>
          </Link>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          {TABS.map((tab, idx) => (
            <button
              key={tab}
              onClick={() => setActiveTab(idx)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === idx
                  ? 'border-orange-600 text-orange-600'
                  : 'border-transparent text-gray-600 hover:text-gray-800'
              }`}
            >
              {idx === 2 && <CalendarDays size={14} />}
              {idx === 1 && <ArrowUpDown size={14} className="opacity-60" />}
              {tab}
              {idx === 1 && bookings.length > 0 && (
                <span className="ml-1 bg-orange-100 text-orange-600 text-xs px-1.5 py-0.5 rounded-full font-bold">
                  {bookings.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 0 && (
          <CreateBookingTab allRooms={allRooms} onCreated={handleBookingCreated} />
        )}
        {activeTab === 1 && (
          <AllBookingsTab
            bookings={bookings}
            loading={bookingsLoading}
            onRefresh={fetchBookings}
            onStatusChange={handleStatusChange}
            onDelete={handleDelete}
          />
        )}
        {activeTab === 2 && (
          <CalendarTab bookings={bookings} />
        )}
      </div>
    </div>
  );
};

export default AdminBookings;
